const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INDIAN_MOBILE_PATTERN = /^[6-9][0-9]{9}$/;
const INDIAN_POSTAL_CODE_PATTERN = /^[1-9][0-9]{5}$/;
const PURCHASE_OUTCOME_KEY = "oath.purchaseOutcome.v1";

const validatedFields = [
  "customerName",
  "customerEmail",
  "customerPhone",
  "addressLine1",
  "city",
  "state",
  "postalCode",
  "country",
];

export function validatePurchaseField(name, rawValue) {
  const value = typeof rawValue === "string" ? rawValue.trim() : "";

  switch (name) {
    case "customerName":
      return value ? "" : "Enter your full name.";
    case "customerEmail":
      if (!value) return "Enter your email address.";
      return EMAIL_PATTERN.test(value) ? "" : "Enter a valid email address.";
    case "customerPhone":
      if (!value) return "Enter your mobile number.";
      return INDIAN_MOBILE_PATTERN.test(value)
        ? ""
        : "Enter a valid 10-digit Indian mobile number.";
    case "addressLine1":
      return value ? "" : "Enter your street address.";
    case "city":
      return value ? "" : "Enter your city.";
    case "state":
      return value ? "" : "Enter your state.";
    case "postalCode":
      if (!value) return "Enter your postal code.";
      return INDIAN_POSTAL_CODE_PATTERN.test(value)
        ? ""
        : "Enter a valid 6-digit Indian PIN.";
    case "country":
      return value.toLowerCase() === "india" ? "" : "Only Indian addresses are supported.";
    default:
      return "";
  }
}

export function validatePurchaseDetails(details) {
  return validatedFields.reduce((errors, field) => {
    const message = validatePurchaseField(field, details[field]);
    if (message) errors[field] = message;
    return errors;
  }, {});
}

/**
 * Converts the local phone field into the exact customer payload expected by
 * POST /api/create-order. Prices and totals remain server-controlled.
 */
export function buildCreateOrderPayload(details, quantity, discountCode) {
  const payload = {
    customerName: details.customerName.trim(),
    customerEmail: details.customerEmail.trim().toLowerCase(),
    customerPhone: `+91${details.customerPhone}`,
    addressLine1: details.addressLine1.trim(),
    addressLine2: details.addressLine2.trim(),
    city: details.city.trim(),
    state: details.state.trim(),
    postalCode: details.postalCode.trim(),
    country: "India",
    quantity,
  };

  if (discountCode) {
    payload.discountCode = discountCode.trim().toUpperCase();
  }

  return payload;
}

export function calculateCheckoutPricing(
  quantity,
  discountPerBookPaise = 0,
) {
  const unitPricePaise = 69_900;
  const shippingAmountPaise = 5_000;
  const bookSubtotalPaise = unitPricePaise * quantity;
  const discountTotalPaise = discountPerBookPaise * quantity;

  return {
    bookSubtotalPaise,
    discountTotalPaise,
    shippingAmountPaise,
    totalAmountPaise:
      bookSubtotalPaise - discountTotalPaise + shippingAmountPaise,
  };
}

const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL ?? "").replace(/\/+$/, "");

export class PurchaseApiError extends Error {
  constructor(message, code, status) {
    super(message);
    this.name = "PurchaseApiError";
    this.code = code;
    this.status = status;
  }
}

export async function postPurchaseApi(path, body) {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(body),
  });

  let payload;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    throw new PurchaseApiError(
      payload?.error?.message ??
        "The payment service could not complete this request.",
      payload?.error?.code ?? "API_ERROR",
      response.status,
    );
  }

  return payload;
}

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function isReceipt(receipt) {
  return (
    receipt &&
    typeof receipt === "object" &&
    isNonEmptyString(receipt.trackingNumber) &&
    isNonEmptyString(receipt.paymentReference) &&
    isNonEmptyString(receipt.sku) &&
    Number.isInteger(receipt.quantity) &&
    receipt.quantity >= 1 &&
    Number.isInteger(receipt.unitPricePaise) &&
    receipt.unitPricePaise >= 0 &&
    Number.isInteger(receipt.shippingAmountPaise) &&
    receipt.shippingAmountPaise >= 0 &&
    Number.isInteger(receipt.totalAmountPaise) &&
    receipt.totalAmountPaise >= 0 &&
    isNonEmptyString(receipt.currency)
  );
}

export function isPurchaseOutcome(outcome) {
  if (!outcome || typeof outcome !== "object") return false;
  if (outcome.status === "success") return isReceipt(outcome.receipt);

  return (
    outcome.status === "failure" &&
    outcome.failure &&
    typeof outcome.failure === "object" &&
    (!outcome.failure.providerOrderId ||
      isNonEmptyString(outcome.failure.providerOrderId)) &&
    (!outcome.failure.paymentReference ||
      isNonEmptyString(outcome.failure.paymentReference))
  );
}

function getSessionStorage(storage) {
  if (storage) return storage;
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function storePurchaseOutcome(outcome, storage) {
  if (!isPurchaseOutcome(outcome)) return false;

  try {
    getSessionStorage(storage)?.setItem(
      PURCHASE_OUTCOME_KEY,
      JSON.stringify(outcome),
    );
    return true;
  } catch {
    return false;
  }
}

export function readPurchaseOutcome(storage) {
  const sessionStorage = getSessionStorage(storage);
  if (!sessionStorage) return null;

  try {
    const rawOutcome = sessionStorage.getItem(PURCHASE_OUTCOME_KEY);
    if (!rawOutcome) return null;
    const outcome = JSON.parse(rawOutcome);
    if (isPurchaseOutcome(outcome)) return outcome;
    sessionStorage.removeItem(PURCHASE_OUTCOME_KEY);
  } catch {
    // A restricted or malformed storage entry should never break checkout.
  }

  return null;
}

export function clearPurchaseOutcome(storage) {
  try {
    getSessionStorage(storage)?.removeItem(PURCHASE_OUTCOME_KEY);
  } catch {
    // Returning home still works when storage is unavailable.
  }
}

let razorpayScriptPromise;

/**
 * Loads the official Checkout script once. Loading it before order creation
 * avoids creating a Razorpay order if Checkout is blocked or unavailable.
 */
export function loadRazorpayCheckout() {
  if (window.Razorpay) return Promise.resolve();
  if (razorpayScriptPromise) return razorpayScriptPromise;

  razorpayScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = "razorpay-checkout-script";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;

    script.addEventListener(
      "load",
      () => {
        if (window.Razorpay) {
          resolve();
          return;
        }

        razorpayScriptPromise = undefined;
        reject(new Error("Razorpay Checkout did not initialize."));
      },
      { once: true },
    );

    script.addEventListener(
      "error",
      () => {
        script.remove();
        razorpayScriptPromise = undefined;
        reject(new Error("Razorpay Checkout could not be loaded."));
      },
      { once: true },
    );

    document.head.appendChild(script);
  });

  return razorpayScriptPromise;
}
